import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Grid,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { calculateCustomMetricForDate } from "../../utils/customMetricCalculator";

const DynamicSystemMetricsChart = ({ startDate, endDate, chartData: parentChartData, tableLoading: parentTableLoading, tableColumnDefinitions, customMetrics }) => {
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [customMetricsList, setCustomMetricsList] = useState([]);
  
  // Use parent chart data if provided, otherwise use empty array
  const chartData = parentChartData || [];
  const loading = parentTableLoading || false;

  // All system metrics with labels
  const allSystemMetrics = [
    { key: "totalSignup", label: "Total Signup" },
    { key: "totalSignout", label: "Total Signout" },
    { key: "accountDeleted", label: "Account Deleted" },
    { key: "totalActiveUsers", label: "Total Active Users" },
    { key: "avgActiveTime", label: "Avg. Active Time" },
    { key: "firstConsultation", label: "First Consultation" },
    { key: "repeatConsultation", label: "Repeat Consultation" },
    { key: "totalPaidChatConsultation", label: "Total Paid Chat Consultation" },
    { key: "totalPaidCallConsultation", label: "Total Paid Call Consultation" },
    { key: "firstCallPaidConsultation", label: "1st Call Paid Consultation" },
    { key: "secondCallPaidConsultation", label: "2nd Call Paid Consultation" },
    { key: "thirdCallPaidConsultation", label: "3rd Call Paid Consultation" },
    { key: "fourthCallPaidConsultation", label: "4th Call Paid Consultation" },
    { key: "fifthCallPaidConsultation", label: "5th Call Paid Consultation" },
    { key: "sixthCallPaidConsultation", label: "6th Call Paid Consultation" },
    { key: "seventhCallPaidConsultation", label: "7th Call Paid Consultation" },
    { key: "firstChatPaidConsultation", label: "1st Chat Paid Consultation" },
    { key: "secondChatPaidConsultation", label: "2nd Chat Paid Consultation" },
    { key: "thirdChatPaidConsultation", label: "3rd Chat Paid Consultation" },
    { key: "fourthChatPaidConsultation", label: "4th Chat Paid Consultation" },
    { key: "fifthChatPaidConsultation", label: "5th Chat Paid Consultation" },
    { key: "sixthChatPaidConsultation", label: "6th Chat Paid Consultation" },
    { key: "seventhChatPaidConsultation", label: "7th Chat Paid Consultation" },
    { key: "freeCanceledChatConsultation", label: "Free Canceled Chat Consultation" },
    { key: "freeCancelledCallConsultation", label: "Free Cancelled Call Consultation" },
    { key: "paidMissedCallConsultation", label: "Paid Missed Call Consultation" },
    { key: "paidMissedChatConsultation", label: "Paid Missed Chat Consultation" },
    { key: "noOfFirstRecharge", label: "No. of First Recharge" },
    { key: "totalNoOfRecharge", label: "Total No. of Recharge" },
    { key: "firstRechargeAmount", label: "First Recharge Amount" },
    { key: "totalRechargeAmount", label: "Total Recharge Amount" },
    { key: "spendOnCall", label: "Spend on Call" },
    { key: "spendOnChat", label: "Spend on Chat" },
    { key: "totalCoinsInWallet", label: "Total Coins in Wallet" },
    { key: "timeSpentOnChat", label: "time spent on chat" },
    { key: "timeSpentOnCall", label: "time spent on Call" },
  ];

  // Color palette for different metrics
  const colors = [
    "#ff8989", "#1976d2", "#2e7d32", "#f44336", "#e91e63", "#ff9800",
    "#4caf50", "#2196f3", "#9c27b0", "#00bcd4", "#795548", "#607d8b",
    "#3f51b5", "#8bc34a", "#ff5722", "#009688", "#cddc39", "#ffc107",
    "#673ab7", "#e91e63", "#00acc1", "#5c6bc0", "#ab47bc", "#ef5350",
    "#26a69a", "#42a5f5", "#66bb6a", "#ffa726", "#ec407a", "#8d6e63",
    "#78909c", "#b0bec5", "#90a4ae", "#546e7a", "#37474f", "#263238"
  ];

  // Get visible system metrics from localStorage
  const getVisibleSystemMetrics = () => {
    try {
      const saved = localStorage.getItem("systemMetricsConfig");
      if (saved) {
        const savedConfig = JSON.parse(saved);
        // Filter to get only visible metrics
        const visibleMetrics = savedConfig
          .filter((metric) => metric.visible !== false)
          .map((metric) => metric.key);
        return visibleMetrics;
      }
      // If no config, return all system metric keys
      return allSystemMetrics.map((m) => m.key);
    } catch (error) {
      console.error("Error loading visible system metrics:", error);
      // Return all system metric keys as fallback
      return allSystemMetrics.map((m) => m.key);
    }
  };

  useEffect(() => {
    setAvailableMetrics(allSystemMetrics);
    
    // Load custom metrics and initialize selection
    const initializeMetrics = () => {
      try {
        const savedCustomMetrics = localStorage.getItem("customMetrics");
        const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
        
        let visibleCustomMetrics = [];
        if (savedCustomMetrics) {
          const customMetricsList = JSON.parse(savedCustomMetrics);
          visibleCustomMetrics = customMetricsList.filter((metric) => {
            const config = customMetricsConfig.find((cm) => cm.id === metric.id);
            return config ? config.visible !== false : true;
          });
          
          setCustomMetricsList(visibleCustomMetrics);
        }
        
        // Get system metrics used in visible custom metrics
        const systemMetricsUsedInCustom = new Set();
        if (visibleCustomMetrics.length > 0) {
          const allSystemMetricKeys = allSystemMetrics.map((m) => m.key);
          visibleCustomMetrics.forEach((customMetric) => {
            if (customMetric.formula) {
              allSystemMetricKeys.forEach((key) => {
                const regex = new RegExp(`\\b${key}\\b`, "g");
                if (regex.test(customMetric.formula)) {
                  systemMetricsUsedInCustom.add(key);
                }
              });
            }
          });
        }
        
        // Pre-select visible system metrics (excluding those used in custom metrics)
        const visibleMetrics = getVisibleSystemMetrics();
        const metricsToSelect = visibleMetrics.filter(
          (key) => !systemMetricsUsedInCustom.has(key)
        );
        
        // Add custom metrics to selection
        const customMetricIds = visibleCustomMetrics.map(m => `custom_${m.id}`);
        const allSelectedMetrics = [...metricsToSelect, ...customMetricIds];
        
        // Ensure at least some metrics are selected by default
        // if (allSelectedMetrics.length > 0) {
        //   setSelectedMetrics(allSelectedMetrics);
        // } else {
        //   // If no visible metrics, select first few important metrics by default
        //   const defaultMetrics = [
        //     'totalSignup',
        //     'totalActiveUsers',
        //     'totalPaidChatConsultation',
        //     'totalPaidCallConsultation'
        //   ].filter(key => allSystemMetrics.some(m => m.key === key));
          
        //   if (defaultMetrics.length > 0) {
        //     setSelectedMetrics(defaultMetrics);
        //   } else if (allSystemMetrics.length > 0) {
        //     // Last resort: select first 4 system metrics
        //     setSelectedMetrics(allSystemMetrics.slice(0, 4).map(m => m.key));
        //   }
        // }
      } catch (error) {
        console.error("Error initializing metrics:", error);
        // Fallback: select first few important metrics
        const defaultMetrics = [
          'totalSignup',
          'totalActiveUsers',
          'totalPaidChatConsultation',
          'totalPaidCallConsultation'
        ].filter(key => allSystemMetrics.some(m => m.key === key));
        
        if (defaultMetrics.length > 0) {
          setSelectedMetrics(defaultMetrics);
        } else if (allSystemMetrics.length > 0) {
          setSelectedMetrics(allSystemMetrics.slice(0, 4).map(m => m.key));
        }
      }
    };
    
    initializeMetrics();
  }, []);

  // Get system metrics used in visible custom metrics (to exclude them)
  const getSystemMetricsUsedInCustomMetrics = () => {
    try {
      const savedCustomMetrics = localStorage.getItem("customMetrics");
      const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
      
      if (!savedCustomMetrics) return new Set();
      
      const customMetricsList = JSON.parse(savedCustomMetrics);
      const visibleCustomMetrics = customMetricsList.filter((metric) => {
        const config = customMetricsConfig.find((cm) => cm.id === metric.id);
        return config ? config.visible !== false : true;
      });
      
      const allSystemMetricKeys = allSystemMetrics.map((m) => m.key);
      const usedKeys = new Set();
      
      visibleCustomMetrics.forEach((customMetric) => {
        if (customMetric.formula) {
          allSystemMetricKeys.forEach((key) => {
            const regex = new RegExp(`\\b${key}\\b`, "g");
            if (regex.test(customMetric.formula)) {
              usedKeys.add(key);
            }
          });
        }
      });
      
      return usedKeys;
    } catch (error) {
      console.error("Error extracting system metrics from custom metrics:", error);
      return new Set();
    }
  };

  // No need to fetch data - using parent chart data
  // The parent component (DashboardMatrixAnalytics) already fetches all table data
  // and passes it as a prop

  const handleMetricChange = (event) => {
    const value = event.target.value;
    setSelectedMetrics(typeof value === "string" ? value.split(",") : value);
  };

  // Prepare chart series data
  const prepareChartData = () => {
    if (!chartData || chartData.length === 0) {
      return { categories: [], series: [] };
    }

    // Get unique dates and sort them
    const dates = [...new Set(chartData.map((item) => item.date))].sort();
    
    // Separate selected metrics into system and custom
    const selectedSystemMetrics = selectedMetrics.filter(m => !m.startsWith('custom_'));
    const selectedCustomMetricIds = selectedMetrics
      .filter(m => m.startsWith('custom_'))
      .map(m => m.replace('custom_', ''));
    
    // Prepare series for system metrics
    const systemSeries = selectedSystemMetrics.map((metricKey, index) => {
      const metric = allSystemMetrics.find((m) => m.key === metricKey);
      const data = dates.map((date) => {
        const dayData = chartData.find((item) => item.date === date);
        return dayData ? dayData[metricKey] || 0 : 0;
      });

      return {
        name: metric ? metric.label : metricKey,
        data: data,
        color: colors[index % colors.length],
      };
    });

    // Prepare series for custom metrics
    const customSeries = selectedCustomMetricIds.map((customMetricId, index) => {
      // Handle both string and number IDs
      const customMetric = customMetricsList.find(m => 
        m.id === Number(customMetricId) || m.id === customMetricId || String(m.id) === String(customMetricId)
      );

      if (!customMetric) return null;
      
      const data = dates.map((date) => {
        const dayData = chartData.find((item) => item.date === date);
        return calculateCustomMetricForDate(customMetric.formula, dayData || {});
      });

      return {
        name: customMetric.name,
        data: data,
        color: colors[(selectedSystemMetrics.length + index) % colors.length],
      };
    }).filter(Boolean);

    // Format dates for display
    const categories = dates.map((date) => {
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    });

    return { categories, series: [...systemSeries, ...customSeries] };
  };

  const { categories, series } = prepareChartData();

  const chartOptions = {
    chart: {
      type: "line",
      height: 450,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
    },
    colors: series.map((s) => s.color),
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 5,
      hover: {
        size: 7,
      },
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
        rotateAlways: false,
        style: {
          fontSize: "12px",
        },
      },
      title: {
        text: "Date",
        style: {
          fontSize: "14px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: "Value",
        style: {
          fontSize: "14px",
          fontWeight: 600,
        },
      },
      labels: {
        formatter: function (val) {
          if (val >= 1000000) {
            return (val / 1000000).toFixed(1) + "M";
          } else if (val >= 1000) {
            return (val / 1000).toFixed(1) + "K";
          }
          return val.toFixed(0);
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return val.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          });
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      floating: true,
      offsetY: -10,
      offsetX: -5,
    },
    grid: {
      borderColor: "#e0e0e0",
      strokeDashArray: 3,
    },
    dataLabels: {
      enabled: false,
    },
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989" }}>
            Dynamic System Metrics Chart
          </Typography>
          {startDate && endDate && (
            <Typography variant="body2" sx={{ color: "#666" }}>
              {new Date(startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} - {new Date(endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </Typography>
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <FormControl fullWidth sx={{ minWidth: 300, maxWidth: 800 }}>
            <InputLabel id="metrics-select-label">Select Metrics</InputLabel>
            <Select
              labelId="metrics-select-label"
              id="metrics-select"
              multiple
              value={selectedMetrics}
              onChange={handleMetricChange}
              input={<OutlinedInput label="Select Metrics" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    // Check if it's a custom metric
                    if (value.startsWith('custom_')) {
                      const customMetricId = value.replace('custom_', '');
                      const customMetric = customMetricsList.find((m) => 
                        String(m.id) === String(customMetricId) || m.id === Number(customMetricId)
                      );
                      return (
                        <Chip
                          key={value}
                          label={customMetric ? customMetric.name : value}
                          size="small"
                          sx={{
                            backgroundColor: "#ff8989",
                            color: "white",
                          }}
                        />
                      );
                    } else {
                      // System metric
                      const metric = allSystemMetrics.find((m) => m.key === value);
                      return (
                        <Chip
                          key={value}
                          label={metric ? metric.label : value}
                          size="small"
                          sx={{
                            backgroundColor: "#ff898915",
                            color: "#ff8989",
                          }}
                        />
                      );
                    }
                  })}
                </Box>
              )}
            >
              {/* System Metrics Section */}
              {availableMetrics.length > 0 && (
                <MenuItem disabled>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#666" }}>
                    System Metrics
                  </Typography>
                </MenuItem>
              )}
              {availableMetrics.map((metric) => (
                <MenuItem key={metric.key} value={metric.key}>
                  <Checkbox checked={selectedMetrics.indexOf(metric.key) > -1} />
                  <ListItemText primary={metric.label} />
                </MenuItem>
              ))}
              
              {/* Custom Metrics Section */}
              {customMetricsList.length > 0 && (
                <MenuItem disabled>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#ff8989" }}>
                    Custom Metrics
                  </Typography>
                </MenuItem>
              )}
              {customMetricsList.map((metric) => (
                <MenuItem key={`custom_${metric.id}`} value={`custom_${metric.id}`}>
                  <Checkbox checked={selectedMetrics.indexOf(`custom_${metric.id}`) > -1} />
                  <ListItemText 
                    primary={metric.name}
                    secondary={metric.formula}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedMetrics.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please select at least one metric to display the chart.
          </Alert>
        ) : loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress sx={{ color: "#ff8989" }} />
          </Box>
        ) : chartData.length === 0 ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No data available for the selected date range and metrics.
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="line"
              height={450}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DynamicSystemMetricsChart;

